/**
 * Sortable module item component with icon, name display, and
 * click navigation to the module editor page.
 */
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	CircleDollarSign,
	Cog,
	Database,
	FileText,
	Folder,
	Globe,
	GripVertical,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	Mail,
	Map,
	Maximize2,
	Megaphone,
	MessageSquare,
	Monitor,
	Pencil,
	PieChart,
	Search,
	Settings,
	ShoppingCart,
	Target,
	Trash2,
	TrendingUp,
	User,
	Users,
	Zap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ModuleItemData {
	id: string;
	name: string;
	slug: string;
	icon: string | null;
	groupId: string | null;
	order: number;
	enabled: boolean;
}

interface ModuleItemProps {
	module: ModuleItemData;
	onEdit: (module: ModuleItemData) => void;
	onDelete: (module: ModuleItemData) => void;
	onToggleEnabled: (module: ModuleItemData) => void;
	onOpenEditor?: (module: ModuleItemData) => void;
}

/** Map of icon names to Lucide icon components. */
const iconMap: Record<string, typeof LayoutDashboard> = {
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	CircleDollarSign,
	Cog,
	Database,
	FileText,
	Folder,
	Globe,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	Mail,
	Map,
	Megaphone,
	MessageSquare,
	Monitor,
	PieChart,
	Search,
	Settings,
	ShoppingCart,
	Target,
	TrendingUp,
	User,
	Users,
	Zap,
};

/**
 * Draggable module item with edit and delete actions.
 * @param props - component props
 * @param props.module - the module data
 * @param props.onEdit - callback when edit is clicked
 * @param props.onDelete - callback when delete is clicked
 * @param props.onToggleEnabled - callback when enabled toggle is clicked
 * @param props.onOpenEditor - callback when full editor is opened
 */
export function ModuleItem({ module, onEdit, onDelete, onToggleEnabled, onOpenEditor }: ModuleItemProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: module.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const Icon = iconMap[module.icon ?? ""] ?? LayoutDashboard;

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={cn(
				"flex items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm font-bold",
				!module.enabled && "opacity-50",
				isDragging && "z-50 shadow-lg"
			)}
		>
			{/* Drag Handle */}
			<button
				{...attributes}
				{...listeners}
				className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
			>
				<GripVertical className="h-4 w-4" />
			</button>

			{/* Icon */}
			<div className="flex h-8 w-8 items-center justify-center rounded bg-muted">
				<Icon className="h-4 w-4" />
			</div>

			{/* Module Info */}
			<div className="flex-1 min-w-0">
				<div className="truncate">{module.name}</div>
				<div className="text-xs text-muted-foreground font-normal">/{module.slug}</div>
			</div>

			{/* Enabled Toggle */}
			<button
				onClick={() => { onToggleEnabled(module); }}
				className={cn(
					"rounded px-2 py-1 text-xs font-medium",
					module.enabled
						? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
						: "bg-muted text-muted-foreground hover:bg-muted/80"
				)}
			>
				{module.enabled ? "Enabled" : "Disabled"}
			</button>

			{/* Actions */}
			<div className="flex gap-1">
				{onOpenEditor && (
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8"
						onClick={() => { onOpenEditor(module); }}
						title="Open full editor"
					>
						<Maximize2 className="h-4 w-4" />
					</Button>
				)}
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8"
					onClick={() => { onEdit(module); }}
					title="Edit module"
				>
					<Pencil className="h-4 w-4" />
				</Button>
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 text-red-400 hover:text-red-400"
					onClick={() => { onDelete(module); }}
					title="Delete module"
				>
					<Trash2 className="h-4 w-4" />
				</Button>
			</div>
		</div>
	);
}
